/**
 * Procedural sandstone facade texture for building walls
 * (docs/plans/2026-07-02-osm-buildings-design.md). Purely cosmetic scene
 * dressing — has NO bearing on any LOS result (hard constraint #1); it only
 * affects how a wall mesh looks, never where anything is placed.
 *
 * `facadeGrid`/`doorSlot` are pure, three.js-free window/door layout maths,
 * unit-tested directly in Node (like routeRibbon's geometry helpers).
 * `makeSandstoneTexture` is a thin three.js/canvas wrapper around them,
 * exercised in e2e rather than unit tests (no real 2D canvas context in the
 * Node test env — see BuildingsLayer's happy-dom polyfill for how it's
 * exercised without crashing under test). Callers must reuse a small fixed
 * set of cached variants (see the module-level cache below), never build a
 * texture per building.
 */
import { CanvasTexture, RepeatWrapping } from 'three';

/** Window rectangle size, metres, in wall-local (x = along wall, y = up from ground) space. */
export const WINDOW_W = 0.8;
export const WINDOW_H = 1.1;

/** Ground-floor door rectangle size, metres. */
export const DOOR_W = 0.9;
export const DOOR_H = 2.0;

const EDGE_MARGIN = 0.6;
const FLOOR_SPACING = 2.6;
const BAY_SPACING = 1.4;

/** Deterministic fractional hash in [0, 1) seeded by an integer (same idiom as turbines.ts). */
function hash01(i: number): number {
  const x = Math.sin(i * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

export interface WindowRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DoorRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Per-building overrides for the primary facade's window grid (issue #116).
 * Both are optional and, when omitted, `facadeGrid` reproduces its default
 * (pre-#116) layout byte-for-byte.
 *  - `rows`   — explicit number of window rows (from appearance.storeys),
 *               clamped to the rows that physically fit (never spills past the
 *               wall top).
 *  - `count`  — target total number of windows (from appearance.window_count),
 *               clamped to grid capacity; `0` renders no windows.
 */
export interface FacadeGridOptions {
  rows?: number;
  count?: number;
}

/**
 * Ground-floor door slot for a wall of the given width, deterministic by
 * seed. Sits at y=0 (ground), horizontally jittered a little around the
 * wall's midpoint so buildings don't all look identical.
 */
export function doorSlot(widthM: number, seed: number): DoorRect {
  const usableJitter = Math.max(0, widthM - 2 * EDGE_MARGIN - DOOR_W);
  const centreX = widthM / 2 + (hash01(seed) - 0.5) * usableJitter * 0.4;
  return { x: centreX - DOOR_W / 2, y: 0, w: DOOR_W, h: DOOR_H };
}

/**
 * Regular grid of window rectangles inset EDGE_MARGIN from the wall edges,
 * deterministic by seed. Column/row count scale with wall width/height.
 * No window overlaps the ground-floor door slot (the ground-floor row skips
 * any column that would collide with it).
 */
export function facadeGrid(widthM: number, heightM: number, seed: number, opts?: FacadeGridOptions): WindowRect[] {
  const out: WindowRect[] = [];
  if (widthM < 2 * EDGE_MARGIN + WINDOW_W || heightM < 2 * EDGE_MARGIN + WINDOW_H) return out;

  const usableW = widthM - 2 * EDGE_MARGIN;
  const cols = Math.max(1, Math.floor(usableW / (WINDOW_W + BAY_SPACING)) + 1);
  const colSpacing = cols > 1 ? usableW / (cols - 1) : 0;

  const usableH = heightM - EDGE_MARGIN;
  const fitRows = Math.max(1, Math.floor(usableH / FLOOR_SPACING));
  // storeys override (issue #116) never spills past the wall top: clamp to the
  // rows that physically fit. No override reproduces the default row count.
  const rows = opts?.rows != null ? Math.max(0, Math.min(Math.floor(opts.rows), fitRows)) : fitRows;

  const door = doorSlot(widthM, seed);

  for (let row = 0; row < rows; row++) {
    const y = EDGE_MARGIN + row * FLOOR_SPACING + (FLOOR_SPACING - WINDOW_H) / 2;
    if (y + WINDOW_H > heightM - EDGE_MARGIN + 1e-9) continue;
    for (let col = 0; col < cols; col++) {
      const cx = cols > 1 ? EDGE_MARGIN + col * colSpacing : widthM / 2;
      const x = cx - WINDOW_W / 2;
      if (row === 0) {
        const overlapsX = x < door.x + door.w && x + WINDOW_W > door.x;
        const overlapsY = y < door.y + door.h && y + WINDOW_H > door.y;
        if (overlapsX && overlapsY) continue;
      }
      out.push({ x, y, w: WINDOW_W, h: WINDOW_H });
    }
  }

  // window_count override (issue #116): take a deterministic prefix of the
  // default grid. Those slots already exclude the door and sit within the wall,
  // so the result never overlaps the door, never spills past an edge, and never
  // exceeds grid capacity (min(count, capacity)). `count: 0` -> no windows.
  if (opts?.count != null) {
    const target = Math.max(0, Math.floor(opts.count));
    return out.slice(0, Math.min(target, out.length));
  }
  return out;
}

const SANDSTONE_BASE = '#c9a877';
const COURSE_LINE = '#a98a5c';
const DOOR_COLOR = '#4a3324';
const PX_PER_M = 32;

// RGB source-of-truth for the baked-in facade palette. The window/frame CSS
// strings below are derived FROM these (never a separately-maintained literal)
// so the canvas-baked texture and the per-building vertex-colour overlays
// (facadeOverlay.ts, buildings.ts buildPrimaryFacadeDetail — issue #116/#123)
// can never drift onto two different window styles (issue #149).
/** Base fill of a sandstone (no-appearance) facade — the overlay backing colour when a building has no recognised wall_material. */
export const SANDSTONE_BASE_RGB: RgbColor = { r: 0xc9, g: 0xa8, b: 0x77 };
/** Sky-reflective blue-grey window glazing — lightened off near-black (issue #149) so panes read as glass rather than dark holes at 20-50 m. */
export const GLAZING_RGB: RgbColor = { r: 0x74, g: 0x8d, b: 0x9e };
/** Light window frame. */
export const FRAME_RGB: RgbColor = { r: 0xf5, g: 0xf2, b: 0xea };
/** Today's fixed door colour (matches DOOR_COLOR); the fallback tint when door_color is null or malformed (issue #116 failure table). */
export const DEFAULT_DOOR_TINT_RGB: RgbColor = { r: 0x4a, g: 0x33, b: 0x24 };

/**
 * Frame member width (m) and sill dimensions (m) shared by every window-
 * drawing path: the canvas-baked texture below AND the vertex-colour overlays
 * in facadeOverlay.ts (re-exported from there for buildings.ts). One number
 * each, not three independently-tuned copies — issue #149 was exactly this
 * kind of drift (a 0.06 m sliver that vanished at distance).
 */
export const FRAME_MARGIN_M = 0.13;
export const SILL_HEIGHT_M = 0.15;
export const SILL_OVERHANG_M = 0.08;

const GLAZING = rgbToCss(GLAZING_RGB);
const FRAME = rgbToCss(FRAME_RGB);

const textureCache = new Map<number, CanvasTexture>();

/**
 * Shared canvas render for a facade texture: base fill + horizontal coursing
 * lines + `facadeGrid` windows (dark glazing + white frame) + one door via
 * `doorSlot`. `baseCss`/`courseCss` are CSS colour strings for the base fill
 * and coursing lines; window/door colours are the same fixed palette for
 * every variant. Used by both `makeSandstoneTexture` (fixed sandstone
 * palette) and `makeFacadeTexture` (appearance-driven family/colour
 * palette, issue #103) so the two never drift apart in layout logic.
 */
function drawFacadeCanvas(
  canvas: HTMLCanvasElement,
  baseCss: string,
  courseCss: string,
  layoutSeed: number,
  widthM: number,
  heightM: number,
  withOpenings = true,
  coursing: FacadeCoursing = 'horizontal',
): void {
  const ctx = canvas.getContext('2d');

  // A 2D context may be unavailable in a headless/test DOM (e.g. happy-dom
  // has no canvas backend) — the texture stays a flat base fill rather than
  // crashing; this is cosmetic-only so a blank variant is harmless.
  if (!ctx) return;

  ctx.fillStyle = baseCss;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.strokeStyle = courseCss;
  ctx.lineWidth = 1;
  // Board/course spacing, ~0.35 m: horizontal lines for masonry/sheet coursing,
  // or vertical lines for timber plank cladding (issue #133).
  const spacing = 0.35 * PX_PER_M;
  if (coursing === 'vertical') {
    for (let x = 0; x < canvas.width; x += spacing) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
  } else {
    for (let y = 0; y < canvas.height; y += spacing) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }
  }

  // Non-domestic treatment (issue #113): a utilitarian family (see
  // familyHasOpenings) is coursed cladding only -- no window grid, no
  // dwelling door slot.
  if (!withOpenings) return;

  const toPx = (x: number, yTop: number) => ({ px: x * PX_PER_M, py: canvas.height - yTop * PX_PER_M });

  const marginPx = FRAME_MARGIN_M * PX_PER_M;
  const sillOverhangPx = SILL_OVERHANG_M * PX_PER_M;
  const sillHeightPx = SILL_HEIGHT_M * PX_PER_M;
  for (const w of facadeGrid(widthM, heightM, layoutSeed)) {
    const { px, py } = toPx(w.x, w.y + w.h);
    const pw = w.w * PX_PER_M;
    const ph = w.h * PX_PER_M;
    ctx.fillStyle = FRAME;
    ctx.fillRect(px - marginPx, py - marginPx, pw + marginPx * 2, ph + marginPx * 2);
    // Sill: a ledge just below the frame, a little wider than it (issue #149 —
    // the dominant close-range cue in reference photos of this area).
    ctx.fillRect(
      px - marginPx - sillOverhangPx,
      py + ph + marginPx,
      pw + marginPx * 2 + sillOverhangPx * 2,
      sillHeightPx,
    );
    ctx.fillStyle = GLAZING;
    ctx.fillRect(px, py, pw, ph);
  }

  const door = doorSlot(widthM, layoutSeed);
  const doorTop = toPx(door.x, door.h);
  ctx.fillStyle = DOOR_COLOR;
  ctx.fillRect(doorTop.px, doorTop.py, door.w * PX_PER_M, door.h * PX_PER_M);
}

/**
 * Builds (or returns a cached) sandstone facade CanvasTexture for the given
 * variant seed: coursed warm-buff sandstone base, `facadeGrid` windows
 * (dark glazing + white frame), and one door. Generate a small fixed set of
 * variants and reuse them across buildings — never one texture per
 * building (design doc "Materials").
 *
 * Wall UVs (see buildWalls in scene/buildings.ts) are metre-based, not
 * normalised to [0,1] — u is the wall-run distance along an edge, v is
 * height above the ground. `wrapS`/`wrapT` are set to RepeatWrapping and
 * `repeat` scaled to 1/widthM, 1/heightM so a UV of `widthM` metres equals
 * exactly one full texture repeat: the facade tiles continuously across a
 * wall instead of clamping to the single texel at UV (0,0) (the bug this
 * fixes — walls previously had no uv attribute at all).
 */
export function makeSandstoneTexture(variantSeed: number, widthM = 10, heightM = 6): CanvasTexture {
  const cached = textureCache.get(variantSeed);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(64, Math.round(widthM * PX_PER_M));
  canvas.height = Math.max(64, Math.round(heightM * PX_PER_M));
  drawFacadeCanvas(canvas, SANDSTONE_BASE, COURSE_LINE, variantSeed, widthM, heightM);

  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(1 / widthM, 1 / heightM);
  texture.needsUpdate = true;
  textureCache.set(variantSeed, texture);
  return texture;
}

// --- Street View-derived appearance: colour quantisation + facade material
// selection (issue #103; design doc
// docs/plans/2026-07-05-streetview-building-appearance-design.md). Pure,
// three.js-free helpers below (hex validation, nearest-fixed-palette colour
// matching, family/bucket resolution) are unit-tested directly in Node, same
// pattern as facadeGrid/doorSlot above. The palette match is the whole
// quantisation mechanism: because each palette has a small fixed size, the
// number of distinct outputs it can produce is bounded by the palette size
// no matter how many distinct input colours are seen (pigeonhole) — this is
// how the hard <=24 shared-material budget (issue #17 perf constraint) holds
// regardless of how many buildings or how varied their real colours are.

export interface RgbColor {
  r: number;
  g: number;
  b: number;
}

/** Parses a `#rrggbb` hex colour (case-insensitive) into 0-255 channel values, or null for anything else — malformed appearance data must fall back, never be guessed at (hard constraint #7). */
export function parseHexColor(hex: string): RgbColor | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

/** Type guard: true only for a well-formed `#rrggbb` string. */
export function isValidHexColor(hex: unknown): hex is string {
  return typeof hex === 'string' && parseHexColor(hex) !== null;
}

function colorDistanceSq(a: RgbColor, b: RgbColor): number {
  return (a.r - b.r) ** 2 + (a.g - b.g) ** 2 + (a.b - b.b) ** 2;
}

/**
 * Index of the closest entry in `palette` to `color` (RGB Euclidean
 * distance; ties keep the earliest index — deterministic). This is the
 * shared quantisation step behind both wall and roof colour matching.
 */
export function nearestPaletteIndex(color: RgbColor, palette: readonly RgbColor[]): number {
  let bestIndex = 0;
  let bestDistSq = Infinity;
  for (let i = 0; i < palette.length; i++) {
    const d = colorDistanceSq(color, palette[i]);
    if (d < bestDistSq) {
      bestDistSq = d;
      bestIndex = i;
    }
  }
  return bestIndex;
}

/**
 * Recognised `wall_material` values that select a facade family (appearance
 * schema amended 2026-07-05 to add `metal`, for corrugated/profiled sheet on
 * agricultural buildings — see the #103 schema-delta note). `"other"` and any
 * unrecognised string are deliberately excluded — both fall back to the
 * hash-based facade per the issue's fallback table.
 */
export const WALL_MATERIAL_FAMILIES = ['stone', 'brick', 'render', 'timber', 'metal'] as const;
export type WallMaterialFamily = (typeof WALL_MATERIAL_FAMILIES)[number];

export function isWallMaterialFamily(value: unknown): value is WallMaterialFamily {
  return typeof value === 'string' && (WALL_MATERIAL_FAMILIES as readonly string[]).includes(value);
}

/**
 * True when `family`'s facade texture is drawn with a window grid + door
 * (every family except `metal`, issue #113). Corrugated/profiled steel sheet
 * reads as utilitarian agricultural/industrial cladding — coursed base +
 * shading only — regardless of which building selects it, whether via the
 * non-domestic no-appearance default or an explicit `wall_material: "metal"`.
 * A pure lookup, independent of any building's domestic/non-domestic status,
 * so it adds no new cache axis to the <=24 shared-material budget: metal's
 * existing 3 buckets simply render without openings everywhere they're used.
 */
export function familyHasOpenings(family: WallMaterialFamily): boolean {
  return family !== 'metal';
}

/** Coursing line orientation for a facade texture. */
export type FacadeCoursing = 'horizontal' | 'vertical';

/**
 * Coursing orientation for a family's facade texture (issue #133): `timber` is
 * drawn as vertical plank/board lines (wood cladding), every other family keeps
 * the horizontal masonry/sheet coursing. Derived from the family alone, so it
 * adds NO new axis to the (family, bucket) facade-texture cache — the <=24
 * shared-material budget (issue #17) is unchanged. A pure lookup, like
 * familyHasOpenings.
 */
export function facadeCoursing(family: WallMaterialFamily): FacadeCoursing {
  return family === 'timber' ? 'vertical' : 'horizontal';
}

/**
 * Small fixed per-family reference tints, dark -> light, that a validated
 * `wall_color` is quantised against. Hand-picked to sit within each family's
 * natural hue range so the bucket a colour lands in still reads as "that
 * kind of material" (design doc: "directionally representative", not
 * photographic). `WALL_MATERIAL_FAMILIES.length * WALL_COLOR_BUCKET_COUNT`
 * is the wall side of the <=24 shared-material budget.
 */
const WALL_PALETTES: Record<WallMaterialFamily, RgbColor[]> = {
  stone: [
    { r: 0x8a, g: 0x83, b: 0x78 },
    { r: 0xc9, g: 0xa8, b: 0x77 },
    { r: 0xe6, g: 0xdd, b: 0xc9 },
  ],
  brick: [
    { r: 0x5c, g: 0x2a, b: 0x20 },
    { r: 0x9c, g: 0x4a, b: 0x34 },
    { r: 0xc9, g: 0x83, b: 0x66 },
  ],
  render: [
    { r: 0x7d, g: 0x77, b: 0x6c },
    { r: 0xc9, g: 0xc2, b: 0xaf },
    { r: 0xef, g: 0xe9, b: 0xdb },
  ],
  timber: [
    { r: 0x4a, g: 0x33, b: 0x24 },
    { r: 0x6b, g: 0x4a, b: 0x30 },
    { r: 0x9c, g: 0x7a, b: 0x52 },
  ],
  // Corrugated/profiled steel sheet (agricultural buildings): anthracite,
  // weathered rust-brown, then galvanized/zinc grey — the "family" hue here
  // is inherently metallic/desaturated rather than warm, unlike the others.
  metal: [
    { r: 0x4a, g: 0x4d, b: 0x50 },
    { r: 0x7a, g: 0x3f, b: 0x2c },
    { r: 0x9a, g: 0x9d, b: 0x9f },
  ],
};

/** Palette size per family (every family's array above has this length). */
export const WALL_COLOR_BUCKET_COUNT = 3;
/** Bucket used when `wall_material` is valid but `wall_color` is missing or malformed — "the material family's default" per the issue's fallback table, never a guess at the actual colour. */
export const DEFAULT_WALL_COLOR_BUCKET = 1;

/** Quantises a validated `wall_color` to the index of `family`'s nearest reference tint, or null if the hex string is malformed. */
export function wallColorBucket(family: WallMaterialFamily, hex: string): number | null {
  const rgb = parseHexColor(hex);
  if (!rgb) return null;
  return nearestPaletteIndex(rgb, WALL_PALETTES[family]);
}

/** The family's reference tint for `bucket` (out-of-range clamps), used as the overlay backing colour (issue #116) so it matches the tiled wall behind it. */
export function wallBaseColor(family: WallMaterialFamily, bucket: number): RgbColor {
  const palette = WALL_PALETTES[family];
  return palette[Math.min(Math.max(0, bucket), palette.length - 1)];
}

/**
 * Maximum RGB Euclidean distance an observed `wall_color` may sit from its
 * nearest family reference tint before it is pulled back toward that tint
 * (issue #150). This is the gamut clamp expressed as DATA — one bound per
 * family, not scattered conditionals — so classifier noise (e.g. a saturated
 * primary mislabelled onto `stone`) can never render an implausible wall, while
 * a genuinely-observed colour sitting between two buckets passes through
 * untouched. Each bound is set a little above the family's largest
 * bucket-to-bucket half-gap so real mid-bucket colours are spent verbatim;
 * `metal` is deliberately tighter, its hue being inherently muted.
 */
export const WALL_TINT_MAX_DISTANCE: Record<WallMaterialFamily, number> = {
  stone: 60,
  brick: 70,
  render: 60,
  timber: 55,
  metal: 45,
};

/**
 * Clamps an observed wall colour into `family`'s plausible gamut (issue #150):
 * if it lies farther than WALL_TINT_MAX_DISTANCE from its nearest family
 * reference tint, it is pulled straight back along the line toward that tint so
 * it sits exactly on the bound; an in-gamut colour is returned unchanged. Pure
 * and deterministic.
 */
export function clampWallTint(family: WallMaterialFamily, rgb: RgbColor): RgbColor {
  const palette = WALL_PALETTES[family];
  const ref = palette[nearestPaletteIndex(rgb, palette)];
  const dr = rgb.r - ref.r;
  const dg = rgb.g - ref.g;
  const db = rgb.b - ref.b;
  const dist = Math.sqrt(dr * dr + dg * dg + db * db);
  const maxD = WALL_TINT_MAX_DISTANCE[family];
  if (dist <= maxD || dist === 0) return { r: rgb.r, g: rgb.g, b: rgb.b };
  const s = maxD / dist;
  return { r: Math.round(ref.r + dr * s), g: Math.round(ref.g + dg * s), b: Math.round(ref.b + db * s) };
}

/**
 * The per-building wall tint carried as a vertex colour (issue #150): a valid
 * observed `wall_color`, clamped into the family's plausible gamut so the real
 * per-building colour is spent rather than quantised to one of three buckets; a
 * null or malformed `wall_color` falls back to the family's default-bucket
 * reference tint — today's bucket behaviour, never a guess (hard constraint #7).
 */
export function wallTintColor(family: WallMaterialFamily, hex: string | null | undefined): RgbColor {
  const rgb = typeof hex === 'string' ? parseHexColor(hex) : null;
  if (!rgb) return wallBaseColor(family, DEFAULT_WALL_COLOR_BUCKET);
  return clampWallTint(family, rgb);
}

/**
 * Fixed door-tint palette a validated `door_color` is quantised against (issue
 * #116). A small representative set of common UK front-door colours; the bucket
 * index is carried as a vertex colour on the single shared overlay material, so
 * this palette size costs no extra materials (unlike the wall/roof texture
 * caches). Entry 0 is today's wood-brown door, which is also the fallback tint
 * for a null/malformed door_color.
 */
export const DOOR_TINT_PALETTE: RgbColor[] = [
  { r: 0x4a, g: 0x33, b: 0x24 }, // wood brown (today's door)
  { r: 0x35, g: 0x5e, b: 0x3b }, // green
  { r: 0x24, g: 0x40, b: 0x5e }, // navy blue
  { r: 0x7a, g: 0x1f, b: 0x1f }, // red
  { r: 0x2b, g: 0x2b, b: 0x2b }, // near-black
  { r: 0xe8, g: 0xe4, b: 0xda }, // off-white
];

/** Quantises a validated `door_color` to the index of its nearest DOOR_TINT_PALETTE entry, or null if the hex string is malformed (never guesses). */
export function doorColorBucket(hex: string): number | null {
  const rgb = parseHexColor(hex);
  if (!rgb) return null;
  return nearestPaletteIndex(rgb, DOOR_TINT_PALETTE);
}

/** The door tint for a (possibly absent/malformed) `door_color`: nearest palette entry for a valid hex, else today's door colour (issue #116 failure table). */
export function doorTintColor(hex: string | null | undefined): RgbColor {
  if (typeof hex !== 'string') return DEFAULT_DOOR_TINT_RGB;
  const bucket = doorColorBucket(hex);
  return bucket === null ? DEFAULT_DOOR_TINT_RGB : DOOR_TINT_PALETTE[bucket];
}

function rgbToCss(c: RgbColor): string {
  return `rgb(${c.r}, ${c.g}, ${c.b})`;
}

function darken(c: RgbColor, factor: number): RgbColor {
  return { r: Math.round(c.r * factor), g: Math.round(c.g * factor), b: Math.round(c.b * factor) };
}

/** Neutral white base for the appearance facade texture (issue #150) so a
 * per-building vertex-colour tint multiplies through to the observed wall colour. */
const NEUTRAL_BASE: RgbColor = { r: 0xff, g: 0xff, b: 0xff };

const facadeTextureCache = new Map<string, CanvasTexture>();

/**
 * Builds (or returns a cached) facade CanvasTexture for a (material family,
 * colour bucket) pair — the appearance-driven analogue of
 * `makeSandstoneTexture`, generalised to family x quantised colour bucket
 * (design doc "Viewer — materials", issue #103). Cached by a string key so
 * the whole viewer never holds more than
 * `WALL_MATERIAL_FAMILIES.length * WALL_COLOR_BUCKET_COUNT` distinct facade
 * textures, regardless of how many buildings request one.
 */
export function makeFacadeTexture(family: WallMaterialFamily, bucket: number, widthM = 10, heightM = 6): CanvasTexture {
  const palette = WALL_PALETTES[family];
  const clampedBucket = Math.min(Math.max(0, bucket), palette.length - 1);
  const key = `${family}:${clampedBucket}`;
  const cached = facadeTextureCache.get(key);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(64, Math.round(widthM * PX_PER_M));
  canvas.height = Math.max(64, Math.round(heightM * PX_PER_M));
  // Layout seed depends only on (family, bucket) — deterministic and shared
  // across every building using this material, never per-building.
  const layoutSeed = WALL_MATERIAL_FAMILIES.indexOf(family) * 1000 + clampedBucket;
  // #150: the base is drawn NEUTRAL (white) so the per-building wall tint carried
  // as a vertex colour (buildings.ts wallTintColor) multiplies through to the
  // observed colour: white * tint = tint, and the coursing (darken 0.75) scales
  // with it. A building with no observed wall_color is tinted by its family/bucket
  // reference (wallBaseColor), reproducing the pre-#150 baked-in look; the
  // per-bucket window layout + coursing orientation still vary by (family, bucket).
  drawFacadeCanvas(
    canvas,
    rgbToCss(NEUTRAL_BASE),
    rgbToCss(darken(NEUTRAL_BASE, 0.75)),
    layoutSeed,
    widthM,
    heightM,
    familyHasOpenings(family),
    facadeCoursing(family),
  );

  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(1 / widthM, 1 / heightM);
  texture.needsUpdate = true;
  facadeTextureCache.set(key, texture);
  return texture;
}

// --- Faint roof coursing (issue #151). Roofs have no windows/doors and no
// per-family layout axis (unlike walls), so this is a much smaller sibling of
// drawFacadeCanvas: a neutral base + horizontal course lines only, tiled by
// RepeatWrapping over a fixed reference tile so it reads consistently across
// any roof size. Drawn NEUTRAL (white base, darken(0.75) lines) exactly like
// makeFacadeTexture's #150 base, so the shared per-bucket MeshStandardMaterial
// `color` (buildings.ts appearanceRoofMaterial / the default roof material)
// tints it — the texture itself never needs to vary by bucket colour, only by
// cache slot (buildings.ts calls this once per roof colour bucket plus one
// reserved slot for the no-appearance default, mirroring the wall cache
// idiom's "never one texture per building").

const ROOF_TILE_M = 3;
const ROOF_COURSE_SPACING_M = 0.32; // approx UK slate/tile coursing gauge

const roofTextureCache = new Map<number, CanvasTexture>();

/**
 * Builds (or returns a cached) faint-coursing CanvasTexture for a roof colour
 * bucket. `bucket` is an opaque cache key here (buildings.ts owns what it
 * means — the 0..ROOF_COLOR_BUCKET_COUNT-1 appearance buckets plus one
 * reserved default slot); this function only needs it to keep cache entries
 * distinct per caller-defined slot, never to key the drawing itself.
 */
export function makeRoofTexture(bucket: number): CanvasTexture {
  const cached = roofTextureCache.get(bucket);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(64, Math.round(ROOF_TILE_M * PX_PER_M));
  canvas.height = canvas.width;
  const ctx = canvas.getContext('2d');
  // Same headless/happy-dom guard as drawFacadeCanvas: no 2D context means a
  // flat (untextured) fill, harmless since this is cosmetic-only.
  if (ctx) {
    ctx.fillStyle = rgbToCss(NEUTRAL_BASE);
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = rgbToCss(darken(NEUTRAL_BASE, 0.75));
    ctx.lineWidth = 1;
    const spacing = ROOF_COURSE_SPACING_M * PX_PER_M;
    for (let y = 0; y < canvas.height; y += spacing) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }
  }

  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(1 / ROOF_TILE_M, 1 / ROOF_TILE_M);
  texture.needsUpdate = true;
  roofTextureCache.set(bucket, texture);
  return texture;
}
